import { isAcTerminalId } from "./ac";
import { getDefinition, type DefLookup } from "./catalog";
import { effectiveTerminals } from "./effective";
import type { ComponentReplacePlan } from "./component-replace";
import type { ComponentNode, System } from "./types";

const TERMINAL_MAP: Readonly<Record<string, string>> = { pos: "ac-l", neg: "ac-n" };

/** Explicit repair of the old battery-side microwave estimate when the drawing
 * already connects it directly to declared AC L/N terminals. Never migrate a
 * DC estimate automatically, infer a voltage, change custom specifications or
 * add a missing PE cable. Manual cable declarations remain untouched. */
export function planLegacyMicrowaveAcConversion(
  system: System,
  nodeId: string,
  voltage: 120 | 230,
  lookup: DefLookup = getDefinition,
): ComponentReplacePlan | null {
  const old = system.nodes.find(node => node.id === nodeId);
  if (old?.kind !== "component" || old.definitionId !== "load-microwave-700" || old.customDefinition) return null;
  const definitionId = `load-microwave-ac-${voltage}`;
  if (!lookup(definitionId)) return null;
  const cables = system.cables.filter(cable => cable.fromNodeId === nodeId || cable.toNodeId === nodeId);
  for (const cable of cables) {
    if (cable.fromNodeId === nodeId && cable.toNodeId === nodeId) return null;
    const from = cable.fromNodeId === nodeId;
    const localTerminalId = from ? cable.fromTerminalId : cable.toTerminalId;
    const expected = localTerminalId === "pos" ? /[-_]l\d?$/i : localTerminalId === "neg" ? /[-_]n$/i : null;
    const remoteId = from ? cable.toNodeId : cable.fromNodeId;
    const remoteTerminalId = from ? cable.toTerminalId : cable.fromTerminalId;
    const remote = system.nodes.find(node => node.id === remoteId);
    if (!expected || remote?.kind !== "component" || !isAcTerminalId(remoteTerminalId) || !expected.test(remoteTerminalId)) return null;
    const remoteDef = lookup(remote.definitionId, remote.customDefinition);
    if (!remoteDef || remoteDef.acVoltage !== voltage) return null;
    const terminal = effectiveTerminals(remoteDef, remote).find(t => t.id === remoteTerminalId);
    if (!terminal || terminal.polarity !== "neutral") return null;
  }

  const remap = <T>(values: Record<string, T> | undefined): Record<string, T> | undefined =>
    values && Object.fromEntries(Object.entries(values).map(([id, value]) => [TERMINAL_MAP[id] ?? id, value]));
  const node: ComponentNode = {
    ...old,
    definitionId,
    ...(old.terminalOverrides ? { terminalOverrides: remap(old.terminalOverrides) } : {}),
    ...(old.terminalLabels ? { terminalLabels: remap(old.terminalLabels) } : {}),
  };
  return {
    node,
    updatedCables: system.cables.map(cable => {
      if (cable.fromNodeId === nodeId) return { ...cable, fromTerminalId: TERMINAL_MAP[cable.fromTerminalId] };
      if (cable.toNodeId === nodeId) return { ...cable, toTerminalId: TERMINAL_MAP[cable.toTerminalId] };
      return cable;
    }),
  };
}
